import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = `${BACKEND_URL}/api`;

// Token storage — deprecated, cookies are primary auth
// Kept only for CSRF token management
const TOKEN_KEY = 'simla_token';

export const getToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export const setToken = (token) => {
  // No longer store JWT — auth is via httpOnly cookies
};

export const clearToken = () => {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {}
};

// CSRF token storage — in-memory only (not sessionStorage for security)
let csrfToken = null;

export function setCsrfToken(token) {
  csrfToken = token;
}

function getCsrfToken() {
  // Try in-memory first (set from login/refresh response)
  if (csrfToken) return csrfToken;
  // Fallback: try reading from cookie (same-origin only)
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Send cookies with every request (primary auth)
  timeout: 120000,
});

// Request interceptor — send CSRF token on state-changing requests
api.interceptors.request.use(
  (config) => {
    if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
      const csrf = getCsrfToken();
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// CSRF retry — if 403 CSRF, fetch token via GET and retry
api.interceptors.response.use(
  (response) => {
    // Capture CSRF token from any response that returns it
    if (response.data?.csrf_token) {
      csrfToken = response.data.csrf_token;
    }
    return response;
  },
  async (err) => {
    if (err.response?.status === 403 && err.response?.data?.detail === 'CSRF validation failed' && !err.config._csrfRetry) {
      err.config._csrfRetry = true;
      // Fetch CSRF token via GET (triggers cookie auto-set)
      try {
        const res = await api.get('/auth/me');
        if (res.data?.csrf_token) csrfToken = res.data.csrf_token;
      } catch {}
      const csrf = getCsrfToken();
      if (csrf) {
        err.config.headers['X-CSRF-Token'] = csrf;
      }
      return api(err.config);
    }
    return Promise.reject(err);
  }
);

// Token refresh logic
let isRefreshing = false;
let failedQueue = [];
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 2;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    // Reset refresh attempts on successful response
    refreshAttempts = 0;
    return response;
  },
  async (err) => {
    const originalRequest = err.config;
    
    // Only handle 401 errors
    if (err.response?.status !== 401) {
      return Promise.reject(err);
    }
    
    // Don't retry auth endpoints - just reject
    const authEndpoints = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/me'];
    if (authEndpoints.some(ep => originalRequest.url?.includes(ep))) {
      return Promise.reject(err);
    }
    
    // Don't retry if already retried
    if (originalRequest._retry) {
      return Promise.reject(err);
    }
    
    // Prevent infinite refresh loops
    if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      console.log('Max refresh attempts reached, redirecting to login');
      clearToken();
      try {
        sessionStorage.removeItem('simla_user');
        localStorage.removeItem('simla_user');
      } catch {}
      refreshAttempts = 0;
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session=expired';
      }
      return Promise.reject(new Error('Session expired. Please sign in again.'));
    }
    
    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => api(originalRequest))
        .catch(err => Promise.reject(err));
    }
    
    // Start refresh — cookies handle token rotation automatically
    originalRequest._retry = true;
    isRefreshing = true;
    refreshAttempts++;
    
    try {
      await api.post('/auth/refresh');
      
      // Process queued requests (cookies are updated automatically)
      processQueue(null);
      refreshAttempts = 0;
      
      // Retry original request (cookies will carry new token)
      return api(originalRequest);
      
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError.response?.data?.detail || refreshError.message);
      
      // Process queued requests with error
      processQueue(refreshError, null);
      
      // Clear auth state
      clearToken();
      try {
        sessionStorage.removeItem('simla_user');
        localStorage.removeItem('simla_user');
      } catch {}
      
      // Redirect to login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session=expired';
      }
      
      return Promise.reject(new Error('Session expired. Please sign in again.'));
      
    } finally {
      isRefreshing = false;
    }
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  requestAccess: (data) => api.post('/auth/request-access', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'),
  listUsers: () => api.get('/auth/users'),
  updateRole: (id, role) => api.put(`/auth/users/${id}/role`, { role }),
  deleteUser: (id) => api.delete(`/auth/users/${id}`),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
};

// Units
export const unitsAPI = {
  list: () => api.get('/units'),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
  bulkCreate: (units) => api.post('/units/bulk', units),
  bulkUpdateStatus: (unitIds, status) => api.post('/units/bulk-status', { unit_ids: unitIds, status }),
};

// Leases
export const leasesAPI = {
  list: () => api.get('/leases'),
  create: (data) => api.post('/leases', data),
  update: (id, data) => api.put(`/leases/${id}`, data),
  delete: (id) => api.delete(`/leases/${id}`),
  renew: (id) => api.post(`/leases/${id}/renew`),
  generateInvoices: (id) => api.post(`/leases/${id}/generate-invoices`),
  getServices: (id) => api.get(`/leases/${id}/services`),
  updateServices: (id, data) => api.put(`/leases/${id}/services`, data),
  importServices: (id, data) => api.post(`/leases/${id}/import-services`, data),
  generateInvoiceFromServices: (id, billingPeriod) => api.post(`/invoices/generate-from-lease/${id}`, null, { params: { billing_period: billingPeriod } }),
};

// Commercial Agreements
export const agreementsAPI = {
  list: () => api.get('/agreements'),
  get: (id) => api.get(`/agreements/${id}`),
  create: (data) => api.post('/agreements', data),
  update: (id, data) => api.put(`/agreements/${id}`, data),
  delete: (id) => api.delete(`/agreements/${id}`),
};

// Clients
export const clientsAPI = {
  list: (search = '') => api.get(`/clients?search=${search}`),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  deletePermanently: (id) => api.delete(`/clients/${id}/permanent`),
  reactivate: (id) => api.put(`/clients/${id}/reactivate`),
};

// Invoices (Sage 300 Compatible)
export const invoicesAPI = {
  list: () => api.get('/invoices'),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  bulkDelete: (invoice_ids) => api.post('/invoices/bulk-delete', { invoice_ids }),
  approve: (id) => api.post(`/invoices/${id}/approve`),
  regenerate: (id) => api.post(`/invoices/${id}/regenerate`),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  downloadCSV: (id) => api.get(`/invoices/${id}/csv`, { responseType: 'blob' }),
  sendToAccountant: (id) => api.post(`/invoices/${id}/send-to-accountant`),
};

// Documents
export const documentsAPI = {
  list: (related_to = '', related_id = '') => api.get(`/documents?related_to=${related_to}&related_id=${related_id}`),
  upload: (formData) => api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  get: (id) => api.get(`/documents/${id}`),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Notifications
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  checkExpiry: () => api.post('/notifications/check-expiry'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  unreadCount: () => api.get('/notifications/unread-count'),
};

// AI
export const aiAPI = {
  analyze: (formData) => api.post('/ai/analyze', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000 // 3 minutes for AI analysis
  }),
  extractLeaseServices: (formData) => api.post('/ai/extract-lease-services', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000 // 3 minutes for AI analysis
  }),
};

// Service Categories
// Admin
export const adminAPI = {
  whitelist: () => api.get('/whitelist'),
  addWhitelist: (data) => api.post('/whitelist', data),
  removeWhitelist: (id) => api.delete(`/whitelist/${id}`),
  auditLog: () => api.get('/audit-log'),
  emailLog: () => api.get('/email-log'),
  accessRequests: () => api.get('/access-requests'),
  pendingAccessCount: () => api.get('/access-requests/pending-count'),
  approveAccess: (id) => api.post(`/access-requests/${id}/approve`),
  rejectAccess: (id, note = '') => api.post(`/access-requests/${id}/reject?note=${encodeURIComponent(note)}`),
  schedulerStatus: () => api.get('/admin/scheduler-status'),
  triggerMonthlyInvoices: () => api.post('/admin/trigger-monthly-invoices'),
  syncFloorUnits: () => api.post('/admin/sync-floor-units'),
};

// Data Sync
export const dataSyncAPI = {
  getEnvironment: () => api.get('/data-sync/environment'),
  exportPreview: () => api.get('/data-sync/export-preview'),
  exportData: () => api.get('/data-sync/export', { responseType: 'blob' }),
  importPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/data-sync/import-preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importData: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/data-sync/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  runMigrations: () => api.post('/data-sync/run-migrations'),
  runSafeMigrations: () => api.post('/data-sync/safe-migrations'),
};

export default api;
